import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiRequestError } from "../../lib/errors";
import {
  getPublicPatIntake,
  savePublicPatIntakeDraft,
  submitPublicPatIntake,
  type PatIntakeAnswers,
} from "../../lib/services/patAutomation";

const EMPTY_ANSWERS: PatIntakeAnswers = {
  has_allergies: null,
  allergy_details: "",
  current_medications: "",
  takes_blood_thinner: null,
  blood_thinner_details: "",
  prior_anesthesia_issue: null,
  anesthesia_issue_details: "",
  has_implants: null,
  implant_details: "",
  health_changes: "",
  callback_phone: "",
};

export function PatIntakePage() {
  const { token = "" } = useParams();
  const queryClient = useQueryClient();
  const [draftState, setDraftState] = useState<{ token: string; answers: PatIntakeAnswers } | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const intakeQuery = useQuery({
    queryKey: ["public-pat-intake", token],
    queryFn: () => getPublicPatIntake(token),
    enabled: token.length > 0,
  });

  const seededAnswers = useMemo(
    () => ({ ...EMPTY_ANSWERS, ...(intakeQuery.data?.answers ?? {}) }),
    [intakeQuery.data?.answers],
  );
  const answers = draftState?.token === token ? draftState.answers : seededAnswers;
  const hasLocalDraft = draftState?.token === token;

  const autosaveMutation = useMutation({
    mutationFn: (draft: PatIntakeAnswers) => savePublicPatIntakeDraft(token, draft),
    onSuccess: async () => {
      setStatusMessage("Draft autosaved.");
      await queryClient.invalidateQueries({ queryKey: ["public-pat-intake", token] });
    },
  });

  const submitMutation = useMutation({
    mutationFn: (draft: PatIntakeAnswers) => submitPublicPatIntake(token, draft),
    onSuccess: async (payload) => {
      setDraftState({ token, answers: payload.answers });
      setStatusMessage("PAT intake submitted.");
      await queryClient.invalidateQueries({ queryKey: ["public-pat-intake", token] });
    },
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasLocalDraft || intakeQuery.data?.intake_status === "submitted") return;
    const handle = window.setTimeout(() => {
      void autosaveMutation.mutateAsync(answers);
    }, 800);
    return () => window.clearTimeout(handle);
  }, [answers, autosaveMutation, hasLocalDraft, intakeQuery.data?.intake_status]);

  if (intakeQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading PAT intake...</div>;
  }

  if (intakeQuery.error instanceof ApiRequestError && [404, 410].includes(intakeQuery.error.status ?? 0)) {
    return (
      <div className="mx-auto mt-12 max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-black text-slate-900">PAT intake unavailable</h1>
        <p className="mt-2 text-sm text-slate-600">This intake link has expired, been revoked, or is otherwise unavailable.</p>
      </div>
    );
  }

  if (intakeQuery.error) {
    return <div className="p-6 text-sm text-red-600">{(intakeQuery.error as Error).message}</div>;
  }

  const intake = intakeQuery.data;
  if (!intake) return null;

  function setBooleanField(key: keyof PatIntakeAnswers, value: boolean) {
    setDraftState({
      token,
      answers: { ...answers, [key]: value },
    });
  }

  function setTextField(key: keyof PatIntakeAnswers, value: string) {
    setDraftState({
      token,
      answers: { ...answers, [key]: value },
    });
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-black text-slate-900">Pre-Admission Intake</h1>
        <p className="mt-2 text-sm text-slate-600">
          {intake.facility_name} · Scheduled {intake.survey_date}
        </p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Draft completion {intake.completion_percent}% · Link expires {new Date(intake.expires_at).toLocaleString()}
        </p>
        {statusMessage ? <p className="mt-3 text-sm text-emerald-700">{statusMessage}</p> : null}

        <div className="mt-6 grid gap-5">
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Current medications</span>
            <textarea className="min-h-24 rounded-lg border border-slate-200 px-3 py-2" value={answers.current_medications ?? ""} onChange={(event) => setTextField("current_medications", event.target.value)} />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Recent health changes</span>
            <textarea className="min-h-24 rounded-lg border border-slate-200 px-3 py-2" value={answers.health_changes ?? ""} onChange={(event) => setTextField("health_changes", event.target.value)} />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Best callback phone</span>
            <input className="rounded-lg border border-slate-200 px-3 py-2" value={answers.callback_phone ?? ""} onChange={(event) => setTextField("callback_phone", event.target.value)} />
          </label>

          {[
            ["has_allergies", "Do you have allergies?"],
            ["takes_blood_thinner", "Do you take blood thinners?"],
            ["prior_anesthesia_issue", "Have you had anesthesia concerns before?"],
            ["has_implants", "Do you have implanted devices?"],
          ].map(([key, label]) => (
            <div key={key} className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-800">{label}</p>
              <div className="mt-3 flex gap-3">
                <button type="button" className={`rounded-lg px-3 py-2 text-sm font-semibold ${answers[key as keyof PatIntakeAnswers] === true ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700"}`} onClick={() => setBooleanField(key as keyof PatIntakeAnswers, true)}>Yes</button>
                <button type="button" className={`rounded-lg px-3 py-2 text-sm font-semibold ${answers[key as keyof PatIntakeAnswers] === false ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-700"}`} onClick={() => setBooleanField(key as keyof PatIntakeAnswers, false)}>No</button>
              </div>
            </div>
          ))}

          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Allergy details</span>
            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2" value={answers.allergy_details ?? ""} onChange={(event) => setTextField("allergy_details", event.target.value)} />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Blood thinner details</span>
            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2" value={answers.blood_thinner_details ?? ""} onChange={(event) => setTextField("blood_thinner_details", event.target.value)} />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Anesthesia issue details</span>
            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2" value={answers.anesthesia_issue_details ?? ""} onChange={(event) => setTextField("anesthesia_issue_details", event.target.value)} />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            <span className="font-semibold">Implant details</span>
            <textarea className="min-h-20 rounded-lg border border-slate-200 px-3 py-2" value={answers.implant_details ?? ""} onChange={(event) => setTextField("implant_details", event.target.value)} />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            disabled={submitMutation.isPending || intake.intake_status === "submitted"}
            onClick={() => void submitMutation.mutateAsync(answers)}
          >
            {intake.intake_status === "submitted" ? "Submitted" : submitMutation.isPending ? "Submitting..." : "Submit Intake"}
          </button>
          <p className="self-center text-xs text-slate-500">Draft changes autosave after a short pause.</p>
        </div>
      </div>
    </div>
  );
}
